import { takeConnectReturn } from './connect-return';
import { EMPTY_PARKED_QUERY, parkedParams, type ParkedQuery } from './parked-tab';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useWorkspaceNavigate } from '../../workspace-path';
import {
  type ConnectOutcome,
  catalogueEntry,
  probeResultSchema,
  integrationInstanceSchema,
  fieldStepSchema,
  instanceTargetsSchema,
  parkedWorklogSchema,
  parkedSummarySchema,
  unroutedTaskSchema,
  integrationRunDetailSchema,
  syncRunResponseSchema,
  type SyncRunResponse,
  remoteStatusChoiceSchema,
  remotePriorityChoiceSchema,
  webhookManualSetupSchema,
  type RemoteStatusChoice,
  type RemotePriorityChoice,
  type WebhookManualSetup,
  integrationRunSchema,
  toWireDate,
  type ExtensionId,
  type IntegrationInstance,
  type IntegrationRun,
  type IntegrationRunDetail,
  type SaveIntegrationRequest,
  type WizardStep,
  type FieldStep,
  type InstanceTargets,
  type ParkedWorklog,
  type ParkedSummary,
  type UnroutedTask,
  type SaveFieldMapsRequest,
  type ProbeRequest,
  type ProbeResult,
} from '@forecastify/shared';
import { Button, Dialog, Toaster, type DateRange, type Toast } from '@forecastify/ui';
import { usePages } from '../use-pages';
import { SyncWindowDialog } from './sync-window-dialog';
import type { RunFilter } from './integrations-screen';

/** `0199` · what each filter asks the server for. Empty is everything. */
const RUN_FILTER_TRIGGERS: Readonly<Record<RunFilter, string>> = {
  /* J-8 · a `target` pull is something a person's target started, so it is theirs to see. */
  people: 'scheduled,manual,backfill,retry,target',
  webhook: 'webhook',
  all: '',
};
import { useT } from '../../i18n';
import type { MessageKey } from '../../i18n/catalogue';
import { INTEGRATION_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { useSession } from '../../session';
import { useInstalls } from './use-installs';
import { useFirstLoad } from '../use-first-load';
import { parseList, parseOne, totalOf, usePermissionSet } from '../permissions-hook';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { SettingsDenied } from '../settings/settings-shell';
import { InstanceWizard } from './instance-wizard';
import {
  IntegrationsCatalogue,
  InstanceDetail,
  ProviderInstances,
  type InstanceTab,
} from './integrations-screen';
import { requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C33 · The three integration addresses, and the one hook behind them.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`integration.manage`, NOT `settings.view` — and the denied body is the ordinary case.** The
 * settings rail shows *Integrációk* to anybody with `settings.view`, because hiding it would make
 * the section invisible to the people who most often ask where it went. What they get on arrival is
 * *"nincs jogosultságod"*, which `settings-shell.tsx` already draws: the read here is a client's
 * Jira URL and a log naming every issue that crossed.
 *
 * ⚠️ **`live` on `EXTENSION_TOPICS` plus `integration`.** An install disappearing from under a
 * half-built instance, and a colleague's manual sync landing in an open run list, are both things
 * this screen has to notice.
 */

let toastCounter = 0;
const nextToastId = (): string => `integration-${String((toastCounter += 1))}`;

interface Vocabulary {
  readonly statuses: readonly { id: string; name: string; color: string }[];
  /** J-5 · the priorities step maps onto these, read exactly as the statuses are. */
  readonly priorities: readonly { id: string; name: string; color: string }[];
  readonly members: readonly { id: string; name: string; email: string }[];
  /**
   * ⚠️ **The task metafields the Fields step maps ONTO**, read here because they are workspace
   * data rather than instance data — which is what lets that step work before an instance exists.
   */
  readonly metafields: FieldStep['metafields'];
}

/** The callback's outcomes, worded once for the profile's connections and reused here. */
const CONNECT_RESULT: Record<ConnectOutcome, MessageKey> = {
  ok: 'account.connections.result.ok',
  cancelled: 'account.connections.result.cancelled',
  refused: 'account.connections.result.refused',
  state: 'account.connections.result.state',
  expired: 'account.connections.result.expired',
  wrong_site: 'account.connections.result.wrong_site',
  no_refresh: 'account.connections.result.no_refresh',
  not_configured: 'account.connections.result.not_configured',
  not_found: 'account.connections.result.not_found',
  malformed: 'account.connections.result.malformed',
  failed: 'account.connections.result.failed',
};

function useIntegrations(): {
  instances: readonly IntegrationInstance[];
  /**
   * ⚠️⚠️ **"THE LIST HAS ARRIVED", WHICH IS NOT THE SAME AS "THE LIST HAS ROWS".** The instance
   * route decides between *"no such instance"* and *"still loading"*, and the first version used
   * `instances.length > 0` as the proxy — so a workspace with none rendered NOTHING AT ALL: no
   * detail, no not-found body, no marker. The route gate caught it as `expected null not to be
   * null`, which is the failure it exists for.
   */
  loaded: boolean;
  /** Until the first answer: skeleton rows and no figures. */
  loading: boolean;
  vocabulary: Vocabulary;
  failure: ScreenFailure | null;
  denied: boolean;
  busy: boolean;
  toasts: readonly Toast[];
  notify: (toast: Toast) => void;
  dismiss: (id: string | number) => void;
  reload: () => Promise<void>;
  /** ⚠️ Two shapes, one button — see the implementation for why the stored one has to exist. */
  probe: (
    request: { kind: 'typed'; body: ProbeRequest } | { kind: 'stored'; instanceId: string },
  ) => Promise<ProbeResult | null>;
  /** `0196` · send the browser to Atlassian to connect the INSTANCE; it comes back to this page. */
  connect: (instanceId: string) => Promise<void>;
  save: (
    request: SaveIntegrationRequest,
    id: string | null,
    /** ⚠️ Field choices made before the instance existed — see the note at the write below. */
    fields?: SaveFieldMapsRequest,
  ) => Promise<boolean>;
  setActive: (instance: IntegrationInstance, active: boolean) => Promise<void>;
  remove: (instance: IntegrationInstance) => Promise<void>;
  /**
   * `0199` · queued, not awaited. No window is *Sync recent* — a month back; a window is a
   * backfill over exactly that span. Answers with the run row to watch, or null on a refusal.
   */
  syncNow: (instance: IntegrationInstance, window?: DateRange) => Promise<SyncRunResponse | null>;
  serverError: string | null;
  clearError: () => void;
} {
  const session = useSession();
  const t = useT();
  const [instances, setInstances] = useState<readonly IntegrationInstance[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [vocabulary, setVocabulary] = useState<Vocabulary>({
    statuses: [],
    priorities: [],
    members: [],
    metafields: [],
  });
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  /**
   * `0196` · the callback lands back here with `?connected=<outcome>`. The outcome becomes one
   * toast and leaves the address bar — the same rule `account-route.tsx` records: a `?connected=ok`
   * left in place re-fires on every reload and gets bookmarked.
   */
  useEffect(() => {
    /* 26 Oct 2026 · the reader the account screen shares, so the new parameters leave here too. */
    const returned = takeConnectReturn();
    if (returned === null) return;
    const code = returned.outcome;
    setToasts((current) => [
      ...current,
      {
        id: `connected-${String(Date.now())}`,
        title: t(CONNECT_RESULT[code]),
        description: returned.detail ?? undefined,
        variant: code === 'ok' ? 'success' : code === 'cancelled' ? 'info' : 'error',
      },
    ]);
  }, [t]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const response = await session.request('/integrations').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (response.status === 404) {
      /* ⚠️ Rule 5 makes a denied read a 404, so this is the DENIED body — not the error state.
         `state-rules.md` keeps them apart: nothing failed, the caller simply may not look. */
      setDenied(true);
      setFailure(null);
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    setInstances(parseList(integrationInstanceSchema, await response.json()) ?? []);
    setLoaded(true);
    setDenied(false);
    setFailure(null);

    /* The two pickers the wizard needs. Both are lists this caller can already see. */
    const [statuses, priorities, members, metafields] = await Promise.all([
      session.request('/task-statuses').catch(() => null),
      session.request('/task-priorities').catch(() => null),
      requestAll(session, '/members').catch(() => null),
      /* ⚠️ `entity=task` only — nothing else can receive a mapped field, and the server's own
         version of this step filters the same way. */
      session.request('/metafields?entity=task').catch(() => null),
    ]);
    const statusRows =
      statuses?.ok === true
        ? ((await statuses.json()) as { data?: { id: string; name: string; color: string }[] }).data
        : undefined;
    const priorityRows =
      priorities?.ok === true
        ? ((await priorities.json()) as { data?: { id: string; name: string; color: string }[] })
            .data
        : undefined;
    const memberRows =
      members?.ok === true
        ? (
            (await members.json()) as {
              data?: { id: string; displayName?: string; name?: string; email?: string }[];
            }
          ).data
        : undefined;
    const metafieldRows =
      metafields?.ok === true
        ? ((await metafields.json()) as { data?: FieldStep['metafields'] }).data
        : undefined;
    setVocabulary({
      metafields: metafieldRows ?? [],
      statuses: statusRows ?? [],
      priorities: priorityRows ?? [],
      members: (memberRows ?? []).map((row) => ({
        id: row.id,
        name: row.displayName ?? row.name ?? row.email ?? row.id,
        email: row.email ?? '',
      })),
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [session.workspaceId]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  useLiveInvalidation(INTEGRATION_TOPICS, () => {
    void reload();
  });

  const toast = (toastValue: Toast): void => {
    setToasts((current) => [...current, toastValue]);
  };

  /**
   * ⚠️⚠️ **EVERY CODE BUT ONE USED TO COME OUT AS *"check your connection"*, WHICH SENT SOMEBODY
   * LOOKING AT THEIR NETWORK FOR A FORM ERROR.** A `validation_failed` from the save is a rule about
   * what was typed — the commonest being a Forecastify status with several Jira statuses pointing
   * at it and no default among them — and answering it with a connectivity sentence is worse than
   * saying nothing: it names a cause that is not the cause. Rule 5 is to branch on `code`, and
   * `unavailable` is what is left when the request never arrived at all.
   */
  const fail = async (response: Response | null): Promise<void> => {
    if (response === null) {
      setServerError(t('error.unavailable'));
      return;
    }
    const body = (await response.json().catch(() => null)) as {
      error?: { code?: string; details?: { field?: string }[] };
    } | null;
    const code = body?.error?.code ?? null;

    /**
     * ⚠️⚠️ **A `conflict` IS NOT ALWAYS THE NAME, AND THE COMMENT THAT SAID IT WAS COST A DAY.**
     * `DuplicateInstanceError` and `StaleWriteError` both answer `conflict`, so a save whose version
     * had moved on reported *an instance with this name already exists* about a name nobody had
     * touched. C40 made that constant rather than rare: the five-minute poll stamps the instance
     * row, and `0093` is the other half of this fix — bookkeeping no longer counts as an edit.
     *
     * ⚠️ **The server already distinguishes them**, on `details[0].field`. Reading it is the whole
     * fix; guessing from the code is what produced a sentence about the wrong thing.
     */
    if (code === 'conflict') {
      const duplicateName = body?.error?.details?.[0]?.field === 'name';
      setServerError(
        t(duplicateName ? 'integrations.error.duplicate' : 'integrations.error.stale'),
      );
      /* ⚠️ A stale write means somebody (or something) else wrote first — so reload, or the next
         attempt fails on the same version and reads as the screen being stuck. */
      if (!duplicateName) await reload();
      return;
    }
    if (code === 'validation_failed') {
      setServerError(t('integrations.error.validation'));
      return;
    }
    setServerError(t('error.unavailable'));
  };

  return {
    instances,
    loaded,
    loading,
    vocabulary,
    failure,
    denied,
    busy,
    toasts,
    /** ⚠️ C34 · the instance route raises its own toasts, so `toast` stops being private here. */
    notify: toast,
    serverError,
    clearError: () => {
      setServerError(null);
    },
    dismiss: (id) => {
      setToasts((current) => current.filter((entry) => entry.id !== id));
    },
    reload,
    /**
     * ⚠️⚠️ **TWO ROUTES FOR ONE BUTTON, AND THE SECOND IS WHY THE FIRST IS NOT ENOUGH.** A typed
     * credential goes to `POST /integrations/probe`, which stores nothing and works before an
     * instance exists. A SAVED instance whose token was not retyped has no token in the browser to
     * send — deliberately — so it goes to `POST /integrations/:id/probe` against the stored one.
     * With only the first, *Test connection* would work exactly when nobody needed it.
     *
     * ⚠️ **The failure lands on `serverError` here**, because this half knows what a 401 from
     * Atlassian means; the wizard's half only needs to know the proof did not happen.
     */
    probe: async (request) => {
      setServerError(null);
      const response = await session
        .request(
          request.kind === 'stored'
            ? `/integrations/${request.instanceId}/probe`
            : '/integrations/probe',
          {
            method: 'POST',
            ...(request.kind === 'typed' ? { body: JSON.stringify(request.body) } : {}),
          },
        )
        .catch(() => null);

      if (response?.ok !== true) {
        await fail(response);
        return null;
      }
      const body = (await response.json()) as { data?: unknown };
      const parsed = probeResultSchema.safeParse(body.data);
      return parsed.success ? parsed.data : null;
    },
    connect: async (instanceId) => {
      setServerError(null);
      const response = await session
        .request(`/integrations/${instanceId}/oauth/instance/start`, {
          method: 'POST',
          /* ⚠️ A PATH, never a URL — the server re-checks it (`oauth-state.ts`). This page. */
          body: JSON.stringify({
            returnTo: `${window.location.pathname}${window.location.search}`,
          }),
        })
        .catch(() => null);
      if (response?.ok !== true) {
        await fail(response);
        return;
      }
      const body = (await response.json()) as { data?: { url?: unknown } };
      if (typeof body.data?.url === 'string') window.location.assign(body.data.url);
    },
    save: async (request, id, fields) => {
      setBusy(true);
      setServerError(null);
      const response = await session
        .request(id === null ? '/integrations' : `/integrations/${id}`, {
          method: id === null ? 'POST' : 'PUT',
          body: JSON.stringify(request),
        })
        .catch(() => null);
      if (response?.ok !== true) {
        setBusy(false);
        await fail(response);
        return false;
      }

      /**
       * ⚠️⚠️ **A NEW CONNECTION DISCOVERS ITSELF, RATHER THAN WAITING FOR SOMEBODY TO PRESS
       * *Discover projects*.** Finishing the wizard and finding an empty Links tab is a dead end
       * that reads as a broken connection — the test two steps earlier just listed the projects, so
       * an empty list here contradicts what the person was shown.
       *
       * ⚠️ **Staged, never linked.** Discovery fills the inbox; a person links (P2-22). This changes
       * WHEN the read happens and nothing about what it does.
       *
       * ⚠️ **Best effort, and its failure is not the save's.** The instance is saved either way, and
       * *Discover projects* is still there — a rate limit or a slow site must not report a
       * successful save as a failure and send somebody round the wizard again.
       */
      const created = ((await response.json()) as { data?: { id?: unknown } }).data;
      const savedId = id ?? (typeof created?.id === 'string' ? created.id : null);

      /**
       * ⚠️⚠️ **THE FIELD MAP IS WRITTEN HERE WHEN THE WIZARD HAD NOWHERE TO PUT IT.** A map row
       * points at an instance id, so a field choice made on a NEW connection had nothing to attach
       * to — the step used to refuse to show anything at all, and once it did show something the
       * choice would have been dropped on save with the step reporting success. The wizard hands
       * them over instead, and this is the first moment an id exists.
       *
       * ⚠️ **Before discovery, deliberately.** Discovery is best-effort and slow; the map is the
       * thing the person actually chose, and it should not queue behind a read that may rate-limit.
       */
      if (savedId !== null && fields !== undefined && fields.maps.length > 0) {
        await session
          .request(`/integrations/${savedId}/fields`, {
            method: 'PUT',
            body: JSON.stringify(fields),
          })
          .catch(() => null);
      }

      if (id === null && savedId !== null) {
        await session
          .request(`/integrations/${savedId}/discover`, { method: 'POST' })
          .catch(() => null);
      }

      setBusy(false);
      await reload();
      toast({
        id: nextToastId(),
        title: t(id === null ? 'integrations.toast.created' : 'integrations.toast.saved'),
        description: request.name,
        variant: 'success',
      });
      return true;
    },
    setActive: async (instance, active) => {
      setBusy(true);
      const response = await session
        .request(`/integrations/${instance.id}/active`, {
          method: 'PATCH',
          body: JSON.stringify({ active, version: instance.version }),
        })
        .catch(() => null);
      setBusy(false);
      if (response?.ok !== true) {
        await fail(response);
        return;
      }
      await reload();
      toast({
        id: nextToastId(),
        title: t(active ? 'integrations.toast.resumed' : 'integrations.toast.paused'),
        description: instance.name,
        variant: active ? 'success' : 'info',
      });
    },
    remove: async (instance) => {
      setBusy(true);
      const response = await session
        .request(`/integrations/${instance.id}`, { method: 'DELETE' })
        .catch(() => null);
      setBusy(false);
      if (response?.ok !== true) {
        await fail(response);
        return;
      }
      await reload();
      toast({
        id: nextToastId(),
        title: t('integrations.toast.deleted'),
        description: t('integrations.toast.deletedBody'),
        variant: 'info',
      });
    },
    syncNow: async (instance, window) => {
      setBusy(true);
      const response = await session
        .request(`/integrations/${instance.id}/runs`, {
          method: 'POST',
          body: JSON.stringify(window === undefined ? {} : { from: window.from, to: window.to }),
        })
        .catch(() => null);
      setBusy(false);
      if (response?.ok !== true) {
        await fail(response);
        return null;
      }
      const answer = parseOne(syncRunResponseSchema, await response.json());
      await reload();
      if (answer !== null) {
        /* ⚠️ The run is QUEUED: the toast says where to watch it, not what it did. */
        toast({
          id: nextToastId(),
          title: answer.queued
            ? t('integrations.toast.queued')
            : t('integrations.toast.alreadyRunning'),
          description: t('integrations.toast.queuedBody'),
          variant: answer.queued ? 'success' : 'info',
        });
      }
      return answer;
    },
  };
}

/* ── the three routes ─────────────────────────────────────────────────────────────────────── */

export function IntegrationsCatalogueRoute() {
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const t = useT();
  const state = useIntegrations();
  const installs = useInstalls();
  /* ⚠️ Item 68 · the switch writes `workspace_extensions`, which is `settings.manage` on the
     server. Asked here so the control is off limits rather than answering 403. */
  const permissions = usePermissionSet();

  if (state.denied) return <SettingsDenied />;

  return (
    <ScreenState failure={state.failure} onRetry={() => void state.reload()}>
      <IntegrationsCatalogue
        loading={state.loading || installs.loading}
        instances={state.instances}
        installed={new Set(installs.installs.map((one) => one.extensionId))}
        canManage={permissions.has('settings.manage')}
        busy={installs.busy}
        onOpenProvider={(id) => {
          go(`/settings/integrations/${id}`);
        }}
        onInstall={installs.install}
        onUninstall={installs.uninstall}
      />
      {/* ⚠️ **TWO toast sources, one Toaster.** The integrations read has its own and item 68's
          switch brought the extension write's along; two `Toaster`s would stack two regions in one
          corner and each would announce itself as *the* live region. */}
      <Toaster
        toasts={[...state.toasts, ...installs.toasts]}
        label={t('settings.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          state.dismiss(id);
          installs.dismiss(id);
        }}
      />
    </ScreenState>
  );
}

export function IntegrationProviderRoute() {
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const t = useT();
  const params = useParams();
  const state = useIntegrations();
  /* ⚠️ Mirrors the server's own `canCreate` on the field step — the same action, asked once. */
  const permissions = usePermissionSet();
  const entry = catalogueEntry(params.extensionId ?? '');
  const [wizard, setWizard] = useState(false);
  const [deleting, setDeleting] = useState<IntegrationInstance | null>(null);

  if (state.denied) return <SettingsDenied />;
  if (entry === null) {
    return (
      <NotFound
        screen="integration-provider"
        onBack={() => {
          go('/settings/integrations');
        }}
      />
    );
  }

  const mine = state.instances.filter((one) => one.extensionId === entry.id);

  return (
    <ScreenState failure={state.failure} onRetry={() => void state.reload()}>
      <ProviderInstances
        loading={state.loading}
        entry={entry}
        instances={mine}
        onBack={() => {
          go('/settings/integrations');
        }}
        onOpen={(id) => {
          go(`/settings/integrations/${entry.id}/${id}`);
        }}
        onNew={() => {
          state.clearError();
          setWizard(true);
        }}
        onSync={(instance) => {
          void state.syncNow(instance);
        }}
        onToggle={(instance, active) => {
          void state.setActive(instance, active);
        }}
        onDelete={setDeleting}
      />

      {/* ⚠️ This wizard only ever CREATES, so there is no instance id and therefore nothing a field
          map could point at. The `fields` step says *save the connection first* rather than
          offering a mapping that could not be stored. */}
      <InstanceWizard
        /* ⚠️ Creating: there is no instance and therefore no credential to ask with, so the
           mapping step falls back to a text box. It becomes a picker once the connection is saved. */
        remoteStatuses={null}
        remotePriorities={null}
        priorities={state.vocabulary.priorities}
        open={wizard}
        entry={entry}
        instance={null}
        singleStep={null}
        fieldStep={null}
        onLoadFields={() => undefined}
        onSaveFields={() => undefined}
        /* ⚠️ Item 67 · an identity row points at an instance id, and this wizard only ever
           creates - so the people step says *save the connection first* rather than offering
           pairings that could not be stored. */
        identities={[]}
        members={state.vocabulary.members}
        onPairIdentity={() => undefined}
        today={toWireDate(new Date())}
        statuses={state.vocabulary.statuses}
        busy={state.busy}
        serverError={state.serverError}
        onClose={() => {
          setWizard(false);
        }}
        onConnect={(instanceId) => {
          void state.connect(instanceId);
        }}
        onProbe={(request) =>
          state.probe(
            request.kind === 'stored'
              ? request
              : {
                  kind: 'typed',
                  body: {
                    extensionId: entry.id,
                    baseUrl: request.draft.baseUrl.trim(),
                    authEmail:
                      request.draft.authEmail.trim() === ''
                        ? undefined
                        : request.draft.authEmail.trim(),
                    authSecret: request.draft.authSecret.trim(),
                  },
                },
          )
        }
        metafields={state.vocabulary.metafields}
        canCreateMetafields={permissions.has('settings.manage')}
        onSave={(request, fields) => {
          void (async () => {
            if (await state.save(request, null, fields)) setWizard(false);
          })();
        }}
      />

      <DeleteDialog
        instance={deleting}
        onClose={() => {
          setDeleting(null);
        }}
        onConfirm={(instance) => {
          void state.remove(instance);
          setDeleting(null);
        }}
      />

      <Toaster
        toasts={[...state.toasts]}
        label={t('settings.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={state.dismiss}
      />
    </ScreenState>
  );
}

/**
 * ⚠️⚠️ **THE TITLE IS TRANSLATED; THE DESCRIPTION IS THE MACHINE CODE, DELIBERATELY.** Rule 7 wants
 * every user-facing string in both languages — and the sentence a failure carries here is written
 * either by us in English or by Atlassian in English, neither of which a Hungarian toast should
 * print. The `code` is not prose: `jira_credential_rejected` needs no translation, is the same word
 * in a support ticket, and is what somebody can actually search for.
 */
async function failureCode(response: Response | null): Promise<string> {
  if (response === null) return 'network';
  const body = (await response.json().catch(() => null)) as { error?: { code?: string } } | null;
  return body?.error?.code ?? String(response.status);
}

/**
 * C34 · the `fields` step's data (P2-20), fetched on demand.
 *
 * ⚠️ **Beside the wizard rather than in the shared hook**, because it belongs to one instance and
 * costs a round trip to the customer's Jira — a list-level cache would fetch for instances nobody
 * ever opened.
 */
function useFieldStep(instanceId: string | null): {
  fieldStep: FieldStep | null;
  load: () => void;
  save: (body: SaveFieldMapsRequest) => void;
} {
  const session = useSession();
  const [fieldStep, setFieldStep] = useState<FieldStep | null>(null);

  const load = useCallback((): void => {
    if (instanceId === null) return;
    void (async () => {
      const response = await session
        .request(`/integrations/${instanceId}/fields`)
        .catch(() => null);
      if (response?.ok !== true) return;
      setFieldStep(parseOne(fieldStepSchema, await response.json()));
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`). */
  }, [instanceId, session.workspaceId]);

  const save = useCallback(
    (body: SaveFieldMapsRequest): void => {
      if (instanceId === null) return;
      void (async () => {
        const response = await session
          .request(`/integrations/${instanceId}/fields`, {
            method: 'PUT',
            body: JSON.stringify(body),
          })
          .catch(() => null);
        if (response?.ok !== true) return;
        setFieldStep(parseOne(fieldStepSchema, await response.json()));
      })();
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- as above: `session` is a fresh object
       every render, so depending on it would rebuild this callback on every one. */
    [instanceId, session.workspaceId],
  );

  return { fieldStep, load, save };
}

export function IntegrationInstanceRoute() {
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const t = useT();
  const params = useParams();
  const session = useSession();
  const state = useIntegrations();
  const permissions = usePermissionSet();

  const [tab, setTab] = useState<InstanceTab>('overview');
  /**
   * C34 · the linking inbox's own read.
   *
   * ⚠️ **Null until it lands, and null is not empty.** *"Nothing discovered"* and *"never looked"*
   * are different facts and the tab draws them differently — a screen that started at `[]` would
   * tell a client their Jira has no projects in it.
   */
  const [links, setLinks] = useState<InstanceTargets | null>(null);
  /* C34 · the `fields` step (P2-20), fetched only when somebody opens it. */
  const fields = useFieldStep(params.instanceId ?? null);
  const [runs, setRuns] = useState<readonly IntegrationRun[]>([]);
  /* `0199` · the run log pages on the server, twenty at a time, and leaves deliveries out. */
  const runPages = usePages(20);
  const [runFilter, setRunFilter] = useState<RunFilter>('people');
  const [syncWindowOpen, setSyncWindowOpen] = useState(false);
  /**
   * C36 · the parked-hours inbox's own read.
   *
   * ⚠️ **Null until it lands**, for the same reason `links` is: *"nothing waiting"* is good news
   * and *"not read yet"* is no news, and a screen that started at `[]` would tell somebody every
   * hour had been filed before it had asked.
   */
  const [parked, setParked] = useState<readonly ParkedWorklog[] | null>(null);
  const [openRun, setOpenRun] = useState<IntegrationRunDetail | null>(null);
  /* 30 Oct 2026 · the open run's records page, fifty at a time, like every list. */
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const recordPages = usePages();
  const [wizardStep, setWizardStep] = useState<WizardStep | null | 'closed'>('closed');
  const [deleting, setDeleting] = useState<IntegrationInstance | null>(null);
  /* C39 · §9.1's manual setup. ⚠️ Holds the one secret this product ever returns, for as long as
     the dialog is open and not a moment longer — there is no way to fetch it again. */
  const [manual, setManual] = useState<WebhookManualSetup | null>(null);
  /**
   * C39 · what Jira calls its statuses, so the mapping step offers them instead of asking somebody
   * to type them.
   *
   * ⚠️ **Null means *we have not been able to ask*, and the step falls back to a text box.** An
   * unsaved instance has no credential to read with, and a Jira that is briefly unreachable must not
   * make the wizard unusable.
   */
  const [remoteStatuses, setRemoteStatuses] = useState<readonly RemoteStatusChoice[] | null>(null);
  /** J-5 · the same for priorities, with the same null. */
  const [remotePriorities, setRemotePriorities] = useState<readonly RemotePriorityChoice[] | null>(
    null,
  );

  const entry = catalogueEntry(params.extensionId ?? '');
  const instance = state.instances.find((one) => one.id === params.instanceId) ?? null;
  const instanceId = params.instanceId ?? '';

  /**
   * ⚠️ **The run log is its own read**, not part of the instance list. A workspace with four
   * instances and a year of history would otherwise ship every run to a screen showing one.
   */
  const { offset: runOffset, limit: runLimit, settle: settleRuns } = runPages;
  const loadRuns = useCallback(async (): Promise<void> => {
    if (instanceId === '') return;
    const query = new URLSearchParams({ limit: String(runLimit) });
    if (runOffset > 0) query.set('offset', String(runOffset));
    const triggers = RUN_FILTER_TRIGGERS[runFilter];
    if (triggers !== '') query.set('trigger', triggers);
    const response = await session
      .request(`/integrations/${instanceId}/runs?${query.toString()}`)
      .catch(() => null);
    if (response?.ok !== true) return;
    const body: unknown = await response.json();
    setRuns(parseList(integrationRunSchema, body) ?? []);
    /* ⚠️ The FILTERED total, from `meta` — `page.ts`' rule, and what the pager counts. */
    const total = (body as { meta?: { total?: unknown } }).meta?.total;
    if (typeof total === 'number') settleRuns(total);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`). */
  }, [instanceId, session.workspaceId, runOffset, runLimit, runFilter, settleRuns]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const runsLoading = useFirstLoad(loadRuns);

  /**
   * The open run and one page of its records.
   *
   * ⚠️ **Paged, 30 Oct 2026.** A three-year backfill wrote tens of thousands of records and the
   * detail read all of them — thirty seconds to open and a page a mile long.
   */
  const { offset: recordOffset, limit: recordLimit, settle: settleRecords } = recordPages;
  useEffect(() => {
    if (openRunId === null || instanceId === '') return;
    const alive = { value: true };
    void (async () => {
      const query = new URLSearchParams({ limit: String(recordLimit) });
      if (recordOffset > 0) query.set('offset', String(recordOffset));
      const response = await session
        .request(`/integrations/${instanceId}/runs/${openRunId}?${query.toString()}`)
        .catch(() => null);
      if (!alive.value || response?.ok !== true) return;
      const detail = parseOne(integrationRunDetailSchema, await response.json());
      setOpenRun(detail);
      if (detail !== null) settleRecords(detail.recordsMeta.total);
    })();
    return () => {
      alive.value = false;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, [openRunId, instanceId, recordOffset, recordLimit, settleRecords]);

  /**
   * ⚠️⚠️ **J-4 · A PAGE OF ROWS AND THE TOTALS OF ALL OF THEM, WHERE THERE WAS ONE READ OF 200.**
   * The tab summed what it held, so Loginet-2's inbox read *"700+ hours"* over exactly 200 rows,
   * oldest first, and every hour parked after early January was out of sight. The rows page like
   * every list; the count, the hours and who they belong to come from `/parked/summary`.
   */
  const parkedPages = usePages();
  const [parkedSummary, setParkedSummary] = useState<ParkedSummary | null>(null);
  const { offset: parkedOffset, limit: parkedLimit, settle: settleParked } = parkedPages;
  /* 17 Oct 2026 · the table's search and filters — the server narrows, the page resets. */
  const [parkedQuery, setParkedQuery] = useState<ParkedQuery>(EMPTY_PARKED_QUERY);
  const loadParked = useCallback(async (): Promise<void> => {
    if (instanceId === '') return;
    const query = new URLSearchParams({ limit: String(parkedLimit) });
    if (parkedOffset > 0) query.set('offset', String(parkedOffset));
    parkedParams(parkedQuery, query);
    const [rows, summary] = await Promise.all([
      session.request(`/integrations/${instanceId}/parked?${query.toString()}`).catch(() => null),
      session.request(`/integrations/${instanceId}/parked/summary`).catch(() => null),
    ]);
    if (rows?.ok === true) {
      const body: unknown = await rows.json();
      setParked(parseList(parkedWorklogSchema, body) ?? []);
      settleParked(totalOf(body));
    }
    if (summary?.ok === true) {
      const parsed = parkedSummarySchema.safeParse(await summary.json().catch(() => null));
      if (parsed.success) setParkedSummary(parsed.data);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`). */
  }, [instanceId, session.workspaceId, parkedOffset, parkedLimit, settleParked, parkedQuery]);

  useEffect(() => {
    void loadParked();
  }, [loadParked]);

  /**
   * J-3 · the synced tasks no line item claims. ⚠️ Paged for real, unlike the parked read above:
   * one untargeted remote project contributes every issue it has.
   */
  const unroutedPages = usePages();
  const [unrouted, setUnrouted] = useState<readonly UnroutedTask[] | null>(null);
  const { offset: unroutedOffset, limit: unroutedLimit, settle: settleUnrouted } = unroutedPages;
  const loadUnrouted = useCallback(async (): Promise<void> => {
    if (instanceId === '') return;
    const query = new URLSearchParams({ limit: String(unroutedLimit) });
    if (unroutedOffset > 0) query.set('offset', String(unroutedOffset));
    const response = await session
      .request(`/integrations/${instanceId}/unrouted?${query.toString()}`)
      .catch(() => null);
    if (response?.ok !== true) return;
    const body: unknown = await response.json();
    setUnrouted(parseList(unroutedTaskSchema, body) ?? []);
    settleUnrouted(totalOf(body));
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`). */
  }, [instanceId, session.workspaceId, unroutedOffset, unroutedLimit, settleUnrouted]);

  useEffect(() => {
    void loadUnrouted();
  }, [loadUnrouted]);

  const loadLinks = useCallback(async (): Promise<void> => {
    if (instanceId === '') return;
    const response = await session.request(`/integrations/${instanceId}/targets`).catch(() => null);
    if (response?.ok !== true) return;
    setLinks(parseOne(instanceTargetsSchema, await response.json()));
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`). */
  }, [instanceId, session.workspaceId]);

  useEffect(() => {
    void loadLinks();
  }, [loadLinks]);

  /**
   * ⚠️ **Asked once the instance exists, and a failure is silent on purpose.** There is no credential
   * before the connection step is saved, and a Jira that is briefly unreachable leaves `null` — which
   * the mapping step reads as *"fall back to a text box"* rather than as an error worth a toast.
   */
  useEffect(() => {
    if (instance === null) return;
    void (async () => {
      const response = await session
        .request(`/integrations/${instance.id}/statuses`)
        .catch(() => null);
      if (response?.ok !== true) return;
      setRemoteStatuses(parseList(remoteStatusChoiceSchema, await response.json()));
    })();
    void (async () => {
      const response = await session
        .request(`/integrations/${instance.id}/priorities`)
        .catch(() => null);
      if (response?.ok !== true) return;
      setRemotePriorities(parseList(remotePriorityChoiceSchema, await response.json()));
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- the id is what identifies it. */
  }, [instance?.id]);

  /* J-3 · `task` too: a rule saved on a contract files tasks out of the unrouted list. */
  useLiveInvalidation(['integration', 'worklog', 'task'], () => {
    void loadUnrouted();
    void loadRuns();
    /* ⚠️ A colleague linking a project has to reach an open inbox — R-19's whole point. */
    void loadLinks();
    /**
     * ⚠️ **`worklog` too, and it is not decoration.** Adoption files a parked hour as a *worklog*
     * write, so a colleague pairing an identity in People management empties rows out of this
     * inbox — and without the second topic the person watching it would still see them waiting.
     */
    void loadParked();
  });

  if (state.denied) return <SettingsDenied />;
  /* ⚠️ **Only once the list has actually ARRIVED** — see `loaded`. A bare `instance === null` would
     flash *"no such instance"* on every arrival, and `instances.length > 0` renders nothing at all
     in a workspace that has none. */
  if (entry === null || (state.failure === null && state.loaded && instance === null)) {
    return (
      <NotFound
        screen="integration-instance"
        onBack={() => {
          go('/settings/integrations');
        }}
      />
    );
  }

  return (
    <ScreenState failure={state.failure} onRetry={() => void state.reload()}>
      {instance === null ? null : (
        <>
          <InstanceDetail
            entry={entry}
            instance={instance}
            tab={tab}
            links={links}
            runs={runs}
            runsLoading={runsLoading}
            runPages={runPages}
            runFilter={runFilter}
            onRunFilter={(next) => {
              setRunFilter(next);
              runPages.reset();
            }}
            openRun={openRun}
            busy={state.busy}
            onBack={() => {
              go('/settings/integrations');
            }}
            onProvider={() => {
              go(`/settings/integrations/${entry.id}`);
            }}
            onTab={(next) => {
              setTab(next);
              setOpenRunId(null);
              setOpenRun(null);
            }}
            /* ⚠️ §5.10 — a single pairing is set on the person's own page now (C39 cleanup). */
            onPeople={() => {
              go('/people');
            }}
            onOpenLineItem={(target) => {
              go(`/contracts/${target.contractIdentifier}/${target.lineItemIdentifier}`);
            }}
            /**
             * ⚠️ **The bulk address match, which is about nobody in particular.** It lost its home
             * when the people list's identity panel went, and it belongs beside the count somebody
             * is already reading. §9.10: on a real site it pairs one account in eleven, so the
             * toast has to say how many rather than implying it solved the problem.
             */
            onMatchPeople={() => {
              void (async () => {
                const response = await session
                  .request('/integrations/identities/match', { method: 'POST' })
                  .catch(() => null);
                if (response?.ok !== true) {
                  state.notify({
                    id: nextToastId(),
                    title: t('identities.failed'),
                    description: await failureCode(response ?? null),
                    variant: 'error',
                  });
                  return;
                }
                const body = (await response.json()) as { data?: { matched?: number } };
                const matched = body.data?.matched ?? 0;
                await state.reload();
                state.notify({
                  id: nextToastId(),
                  title:
                    matched === 0
                      ? t('identities.noMatch')
                      : t('identities.matched', { count: matched }),
                  variant: matched === 0 ? 'info' : 'success',
                });
              })();
            }}
            parked={parked}
            parkedSummary={parkedSummary}
            parkedPages={parkedPages}
            parkedQuery={parkedQuery}
            onParkedQuery={(next) => {
              setParkedQuery(next);
              parkedPages.reset();
            }}
            unrouted={unrouted}
            unroutedPages={unroutedPages}
            onOpenTask={(key) => {
              go(`/tasks/${key}`);
            }}
            onContracts={() => {
              go('/contracts');
            }}
            /**
             * C39 · turn live updates on, or repair them.
             *
             * ⚠️⚠️ **THE SAME BUTTON DOES BOTH, BECAUSE RE-REGISTERING *IS* THE REPAIR.** It
             * rotates the URL token and the signing secret and deletes the old subscription at
             * Jira — so *"the deployment moved"*, *"the tunnel restarted"* and *"an admin deleted
             * it"* all have one answer, and a screen with two buttons for one action would be
             * asking somebody to diagnose before they can fix.
             */
            /**
             * §9.1's fallback. ⚠️⚠️ **THE RESPONSE CARRIES THE SIGNING SECRET AND NOTHING ELSE EVER
             * WILL**, so it is held in component state for exactly as long as the dialog is open
             * and never re-fetched. Closing it is the point of no return, which the copy says.
             */
            onManualWebhook={() => {
              void (async () => {
                const response = await session
                  .request(`/integrations/${instance.id}/webhook/manual`, { method: 'POST' })
                  .catch(() => null);
                if (response?.ok !== true) {
                  state.notify({
                    id: nextToastId(),
                    title: t('integrations.webhook.failed'),
                    description: await failureCode(response ?? null),
                    variant: 'error',
                  });
                  return;
                }
                const parsed = parseOne(
                  webhookManualSetupSchema,
                  (await response.json()) as unknown,
                );
                if (parsed === null) {
                  state.notify({
                    id: nextToastId(),
                    title: t('integrations.webhook.failed'),
                    variant: 'error',
                  });
                  return;
                }
                setManual(parsed);
                await state.reload();
              })();
            }}
            onRegisterWebhook={() => {
              void (async () => {
                const response = await session
                  .request(`/integrations/${instance.id}/webhook`, { method: 'POST' })
                  .catch(() => null);
                if (response?.ok !== true) {
                  state.notify({
                    id: nextToastId(),
                    title: t('integrations.webhook.failed'),
                    /* ⚠️ The code, because `misconfigured` and `conflict` are two different jobs
                       for two different people — one is the operator's, one is the customer's. */
                    description: await failureCode(response ?? null),
                    variant: 'error',
                  });
                  return;
                }
                await state.reload();
                state.notify({
                  id: nextToastId(),
                  title: t('integrations.webhook.registered'),
                  variant: 'success',
                });
              })();
            }}
            onDisableWebhook={() => {
              void (async () => {
                const response = await session
                  .request(`/integrations/${instance.id}/webhook`, { method: 'DELETE' })
                  .catch(() => null);
                if (response?.ok !== true) {
                  state.notify({
                    id: nextToastId(),
                    title: t('integrations.webhook.failed'),
                    description: await failureCode(response ?? null),
                    variant: 'error',
                  });
                  return;
                }
                await state.reload();
                state.notify({
                  id: nextToastId(),
                  title: t('integrations.webhook.disabled'),
                  variant: 'info',
                });
              })();
            }}
            onRunOverwatch={() => {
              void (async () => {
                const response = await session
                  /* ⚠️ The same route the header's *Sync now* posts to. One action, one endpoint —
                     two buttons that both read *sync* must not mean two different things. */
                  .request(`/integrations/${instance.id}/runs`, { method: 'POST' })
                  .catch(() => null);
                /**
                 * ⚠️ **429 is the daily cap, not a failure**, and it says so — a run refused with
                 * a red *"failed"* is a run somebody presses again.
                 */
                if (response?.status === 429) {
                  state.notify({
                    id: nextToastId(),
                    title: t('integrations.parked.tooSoon'),
                    variant: 'info',
                  });
                  return;
                }
                if (response?.ok !== true) {
                  state.notify({
                    id: nextToastId(),
                    title: t('integrations.parked.failed'),
                    description: await failureCode(response ?? null),
                    variant: 'error',
                  });
                  return;
                }
                await Promise.all([loadParked(), loadRuns()]);
                /* ⚠️ `0199` · queued now, so the toast says where to watch it, not that it ran. */
                state.notify({
                  id: nextToastId(),
                  title: t('integrations.toast.queued'),
                  description: t('integrations.toast.queuedBody'),
                  variant: 'success',
                });
              })();
            }}
            onDiscardParked={(ids) => {
              void (async () => {
                const response = await session
                  .request(`/integrations/${instance.id}/parked/discard`, {
                    method: 'POST',
                    body: JSON.stringify({ ids }),
                  })
                  .catch(() => null);
                if (response?.ok !== true) {
                  state.notify({
                    id: nextToastId(),
                    title: t('integrations.parked.failed'),
                    description: await failureCode(response ?? null),
                    variant: 'error',
                  });
                  return;
                }
                /* J-4 · re-read the page AND the totals: the response is only the first page. */
                await loadParked();
                state.notify({
                  id: nextToastId(),
                  title: t('integrations.parked.discarded'),
                  variant: 'success',
                });
              })();
            }}
            onDiscover={() => {
              void (async () => {
                const response = await session
                  .request(`/integrations/${instance.id}/discover`, { method: 'POST' })
                  .catch(() => null);
                /* ⚠️ The failure body carries the code — a private URL, a dead credential, a
                   throttle — and `state.toast` is where a reader finds out which. */
                if (response?.ok !== true) {
                  state.notify({
                    id: nextToastId(),
                    title: t('integrations.links.failed'),
                    description: await failureCode(response ?? null),
                    variant: 'error',
                  });
                  return;
                }
                await loadLinks();
                state.notify({
                  id: nextToastId(),
                  title: t('integrations.links.discovered'),
                  variant: 'success',
                });
              })();
            }}
            recordPages={recordPages}
            onOpenRun={(runId) => {
              recordPages.reset();
              setOpenRunId(runId);
            }}
            onCloseRun={() => {
              setOpenRunId(null);
              setOpenRun(null);
            }}
            onSync={() => {
              void (async () => {
                const answer = await state.syncNow(instance);
                /* ⚠️ `0199` · straight to the LIST, where the queued row sits on top in its
                   `running` state — the run has not happened yet, so there is no detail to open.
                   The live topic refreshes the row when the worker finishes it. */
                if (answer !== null) {
                  setRunFilter('people');
                  runPages.reset();
                  setOpenRunId(null);
                  setOpenRun(null);
                  setTab('runs');
                }
                await loadRuns();
              })();
            }}
            onSyncWindow={() => {
              setSyncWindowOpen(true);
            }}
            onToggle={(active) => {
              void state.setActive(instance, active);
            }}
            onEdit={(step) => {
              state.clearError();
              setWizardStep(step);
            }}
            onDelete={() => {
              setDeleting(instance);
            }}
          />

          <InstanceWizard
            remoteStatuses={remoteStatuses}
            remotePriorities={remotePriorities}
            priorities={state.vocabulary.priorities}
            open={wizardStep !== 'closed'}
            entry={entry}
            instance={instance}
            singleStep={wizardStep === 'closed' ? null : wizardStep}
            fieldStep={fields.fieldStep}
            onLoadFields={fields.load}
            onSaveFields={fields.save}
            /**
             * ⚠️⚠️ **THE INSTANCE'S OWN IDENTITY ROWS, WHICH THE SCREEN ALREADY HELD - item 67.**
             * `integrationInstanceSchema.users` has carried them since C34 for the *"n of m
             * paired"* card; the wizard simply never got them. No new request.
             */
            /* ⚠️ Not `instance?.users ?? []` — by this line the route has already returned its
               not-found body when `instance` is null, so the optional chain is dead code and the
               lint rule says so. */
            identities={instance.users}
            members={state.vocabulary.members}
            onPairIdentity={(identityId, memberId) => {
              /* ⚠️ Written immediately rather than held in the draft: a pairing is a fact about
                 two systems, not a setting on this form, and a draft would lose it if somebody
                 closed the wizard on the last step. `state.reload()` is what refreshes the *n of
                 m* card behind it. */
              void (async () => {
                await session
                  .request(`/integrations/identities/${identityId}`, {
                    method: 'PUT',
                    body: JSON.stringify({ memberId }),
                  })
                  .catch(() => null);
                await state.reload();
              })();
            }}
            today={toWireDate(new Date())}
            statuses={state.vocabulary.statuses}
            busy={state.busy}
            serverError={state.serverError}
            onClose={() => {
              setWizardStep('closed');
            }}
            onConnect={(instanceId) => {
              void state.connect(instanceId);
            }}
            onProbe={(request) =>
              state.probe(
                request.kind === 'stored'
                  ? request
                  : {
                      kind: 'typed',
                      body: {
                        extensionId: entry.id,
                        baseUrl: request.draft.baseUrl.trim(),
                        authEmail:
                          request.draft.authEmail.trim() === ''
                            ? undefined
                            : request.draft.authEmail.trim(),
                        authSecret: request.draft.authSecret.trim(),
                      },
                    },
              )
            }
            metafields={state.vocabulary.metafields}
            canCreateMetafields={permissions.has('settings.manage')}
            onSave={(request, fields) => {
              void (async () => {
                if (await state.save(request, instance.id, fields)) setWizardStep('closed');
              })();
            }}
          />

          <ManualDialog
            setup={manual}
            onClose={() => {
              setManual(null);
            }}
          />
          <SyncWindowDialog
            open={syncWindowOpen}
            sinceOn={instance.sinceOn}
            busy={state.busy}
            onClose={() => {
              setSyncWindowOpen(false);
            }}
            onConfirm={(range) => {
              void (async () => {
                const answer = await state.syncNow(instance, range);
                if (answer !== null) {
                  setSyncWindowOpen(false);
                  setRunFilter('people');
                  runPages.reset();
                  setOpenRunId(null);
                  setOpenRun(null);
                  setTab('runs');
                }
                await loadRuns();
              })();
            }}
          />

          <DeleteDialog
            instance={deleting}
            onClose={() => {
              setDeleting(null);
            }}
            onConfirm={(target) => {
              void (async () => {
                await state.remove(target);
                setDeleting(null);
                go(`/settings/integrations/${entry.id}`);
              })();
            }}
          />
        </>
      )}

      <Toaster
        toasts={[...state.toasts]}
        label={t('settings.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={state.dismiss}
      />
    </ScreenState>
  );
}

/**
 * §9.1's manual setup, shown once.
 *
 * ⚠️⚠️ **THE SECRET IS ON SCREEN AND NOWHERE ELSE — THERE IS NO SECOND CHANCE TO READ IT.** That is
 * a deliberate property rather than a limitation: a secret a screen can re-display is a secret
 * stored somewhere it can be re-read, and this one is sealed the moment it is written. So the copy
 * has to say what closing this costs, before somebody closes it to go and find their Jira tab.
 *
 * ⚠️ **Four values, in the order Jira's form asks for them**, because the person is copying from
 * here into that form field by field and any other order makes them hunt.
 */
function ManualDialog({
  setup,
  onClose,
}: {
  readonly setup: WebhookManualSetup | null;
  readonly onClose: () => void;
}) {
  const t = useT();

  const rows =
    setup === null
      ? []
      : [
          { k: t('integrations.webhook.manualUrl'), v: setup.url },
          { k: t('integrations.webhook.manualSecret'), v: setup.secret },
          { k: t('integrations.webhook.manualEvents'), v: setup.events.join(', ') },
          { k: t('integrations.webhook.manualFilter'), v: setup.filter },
        ];

  return (
    <Dialog
      open={setup !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('integrations.webhook.manualTitle')}
      description={t('integrations.webhook.manualNote')}
      closeLabel={t('common.close')}
    >
      <div className="mt-1 flex flex-col gap-2">
        {rows.map((row) => (
          <div key={row.k} className="flex flex-col gap-1">
            <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
              {row.k}
            </span>
            {/* ⚠️ `select-all` and a wrapping box: these are long strings somebody is copying by
                hand, and a truncated one copies truncated. */}
            <code className="bg-muted rounded-lg p-2 text-[11.5px] break-all select-all">
              {row.v}
            </code>
          </div>
        ))}
        <p className="text-destructive mt-1 text-[11.5px]">
          {t('integrations.webhook.manualWarn')}
        </p>
        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1" />
          <Button size="lg" onClick={onClose}>
            {t('integrations.webhook.manualDone')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function DeleteDialog({
  instance,
  onClose,
  onConfirm,
}: {
  readonly instance: IntegrationInstance | null;
  readonly onClose: () => void;
  readonly onConfirm: (instance: IntegrationInstance) => void;
}) {
  const t = useT();

  return (
    <Dialog
      open={instance !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('integrations.deleteTitle')}
      /* ⚠️ The body says what SURVIVES, because that is what nobody expects: the tasks and hours
         already synchronised are the workspace's own records now. */
      description={t('integrations.deleteBody')}
      closeLabel={t('common.close')}
    >
      <div className="mt-1 flex items-center gap-2">
        <div className="flex-1" />
        <Button variant="outline" size="lg" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="destructive"
          size="lg"
          onClick={() => {
            if (instance !== null) onConfirm(instance);
          }}
        >
          {t('integrations.delete')}
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * ⚠️⚠️ **IT CARRIES THE ROUTE'S OWN MARKER, WHICH IS THE WHOLE REASON IT TAKES ONE.** The route
 * gate asserts each address renders ITS screen; a shared not-found body with one hard-coded marker
 * makes the instance route claim to be the provider route, and the failure reads
 * `expected null not to be null` — which is a message about a missing screen, not a wrong one.
 */
function NotFound({ screen, onBack }: { readonly screen: string; readonly onBack: () => void }) {
  const t = useT();
  return (
    <div
      data-screen={screen}
      className="mx-auto mt-10 flex max-w-[420px] flex-col items-center gap-2 px-5 text-center"
    >
      <p className="text-[13px]">{t('integrations.notFound')}</p>
      <Button variant="outline" size="sm" onClick={onBack}>
        {t('integrations.heading')}
      </Button>
    </div>
  );
}

export type { ExtensionId };
